import Auth from "@aws-amplify/auth"
import { CognitoUser } from "amazon-cognito-identity-js"
import * as React from "react"
import { Maybe, MibUserQueryMibUser, UpdateMibUserMutationUpdateMibUser } from "../../graphql/generated-types"
import { publicEnvVars } from "../../utils/config"
import { currentAuthenticatedUser } from "../screens/auth/cognito"
import { Router as RouterProps } from "next/dist/client/router"
import { gaPageView } from "../../utils/analytics"
import { storeGoogleOrderingToken } from "./utils"

interface State {
    isInitialized: boolean
    cognitoUserData: CognitoUser
    mibUser: Maybe<MibUserQueryMibUser>
}

export enum AuthProvider {
    Facebook = "facebook",
    Google = "google",
    Cognito = "cognito",
}

export interface AuthInfoContextInterface {
    userId: string
    name: string
    isLoggedIn: boolean
    userEmail: string
    userPhoto?: string // Only for external auth (FB/Google)
    accessToken: string
    provider: AuthProvider
    isInitialized: boolean
    mibUser: Maybe<MibUserQueryMibUser>
    setMibUserData: (mibUser: MibUserQueryMibUser | UpdateMibUserMutationUpdateMibUser) => void
    checkIsAuthenticated: (callback?: () => void) => void
}

export const AuthInfoContext = React.createContext<AuthInfoContextInterface | null>(null)

type Props = {
    children: React.ReactNode
    router: RouterProps
}

class AuthInfoProvider extends React.Component<Props, State> {
    mounted = false
    amplifyConfigured = false

    state = {
        isInitialized: false,
        cognitoUserData: null,
        mibUser: null,
    }

    safeSetState = (state, callback?: () => void) => {
        if (this.mounted) {
            return this.setState(state, () => {
                if (callback) {
                    callback()
                }
            })
        }
    }

    componentDidMount() {
        this.mounted = true
        this.configureCognitoAmplify()

        // GA User tracking
        this.props.router.events.on("routeChangeComplete", this.onRouteChanged)

        // Store 'Google Ordering Button' token
        storeGoogleOrderingToken()
    }

    componentWillUnmount() {
        this.mounted = false
        this.props.router.events.off("routeChangeComplete", this.onRouteChanged)
    }

    onRouteChanged = (url: Maybe<string>) => {
        gaPageView(url, this.state.mibUser?.id)
    }

    configureCognitoAmplify = () => {
        if (!this.amplifyConfigured) {
            Auth.configure({
                identityPoolId: publicEnvVars.COGNITO_IDENTITY_POOL_ID,
                region: "us-east-1",
                identityPoolRegion: "us-east-1",
                userPoolId: publicEnvVars.COGNITO_USER_POOL_ID,
                userPoolWebClientId: publicEnvVars.COGNITO_CLIENT_ID,
            })
            this.amplifyConfigured = true
            this.handleCheckIsAuthenticated()
        }
    }

    handleCheckIsAuthenticated = (callback?: () => void) => {
        if (this.amplifyConfigured) {
            currentAuthenticatedUser()
                .then((userData) => {
                    this.safeSetState({ cognitoUserData: userData, isInitialized: true }, callback)
                })
                .catch(() => this.safeSetState({ cognitoUserData: null, isInitialized: true, mibUser: null }))
        }
    }

    setMibUserData = (mibUser: MibUserQueryMibUser) => this.setState({ mibUser: mibUser })

    render() {
        const { cognitoUserData } = this.state

        let contextContent: AuthInfoContextInterface = {
            userId: null,
            userEmail: null,
            name: null,
            isLoggedIn: false,
            provider: null,
            accessToken: null,
            mibUser: this.state.mibUser,
            checkIsAuthenticated: this.handleCheckIsAuthenticated,
            setMibUserData: this.setMibUserData,
            isInitialized: this.state.isInitialized,
        }

        if (cognitoUserData && cognitoUserData.attributes) {
            // Authed with Email (email/password)
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.username,
                userEmail: cognitoUserData.attributes.email,
                name: cognitoUserData.attributes.name,
                isLoggedIn: true,
                accessToken: cognitoUserData.signInUserSession.idToken.jwtToken,
                provider: AuthProvider.Cognito,
            }
        } else if (cognitoUserData && cognitoUserData.provider === AuthProvider.Google) {
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.userId,
                userEmail: cognitoUserData.userEmail,
                name: cognitoUserData.name,
                userPhoto: cognitoUserData.userPhoto,
                isLoggedIn: true,
                accessToken: cognitoUserData.accessToken,
                provider: cognitoUserData.provider,
            }
        } else if (cognitoUserData && cognitoUserData.provider === AuthProvider.Facebook) {
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.userId,
                userEmail: cognitoUserData.userEmail,
                name: cognitoUserData.name,
                userPhoto: cognitoUserData.userPhoto,
                isLoggedIn: true,
                accessToken: cognitoUserData.accessToken,
                provider: cognitoUserData.provider,
            }
        }

        return <AuthInfoContext.Provider value={contextContent}>{this.props.children}</AuthInfoContext.Provider>
    }
}

const AuthInfoConsumer = AuthInfoContext.Consumer

export { AuthInfoConsumer, AuthInfoProvider }
